import { useState } from "react";
import { Link } from "react-router-dom";
import { Star, ShieldCheck, Clock, Award, Wrench, Zap, Thermometer, ArrowRight } from "lucide-react";
import BeforeAfterSlider from "@/components/BeforeAfterSlider";
import { EstimateButton } from "@/components/CTAButtons";
import { trustSignals, testimonials, projects, services } from "@/data/site";

const icons = { ShieldCheck, Clock, Award, Star, Wrench, Zap, Thermometer };

// ---------- Trust signal bar ----------
export const TrustBar = () => (
  <section className="bg-white border-y border-[#E2E8F0]" data-testid="trust-bar">
    <div className="max-w-7xl mx-auto px-4 grid grid-cols-2 lg:grid-cols-4 divide-x divide-[#E2E8F0]">
      {trustSignals.map((t) => {
        const Icon = icons[t.icon];
        return (
          <div key={t.label} className="flex items-center gap-3 justify-center py-6 px-3 text-center sm:text-left">
            <Icon className="w-7 h-7 text-[#059669] shrink-0" strokeWidth={2} />
            <span className="font-display font-bold text-sm sm:text-base text-[#0F2E4C]">{t.label}</span>
          </div>
        );
      })}
    </div>
  </section>
);

// ---------- Service preview cards ----------
export const ServiceCards = () => (
  <div className="grid grid-cols-1 md:grid-cols-3 gap-6" data-testid="service-cards">
    {services.map((s) => {
      const Icon = icons[s.icon];
      return (
        <Link
          key={s.slug}
          to={`/services/${s.slug}`}
          className="group bg-white border border-[#E2E8F0] hover:border-[#0F2E4C] hover:-translate-y-1 hover:shadow-[6px_6px_0px_#0F2E4C] transition-all duration-200 flex flex-col"
          data-testid={`service-card-${s.slug}`}
        >
          <div className="relative h-52 overflow-hidden">
            <img src={s.image} alt={`${s.name} services in New York`} loading="lazy" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
            <div className="absolute top-4 left-4 w-12 h-12 bg-[#059669] flex items-center justify-center">
              <Icon className="w-6 h-6 text-white" strokeWidth={2.5} />
            </div>
          </div>
          <div className="p-6 flex flex-col flex-1">
            <h3 className="font-display font-bold text-2xl mb-1">{s.name}</h3>
            <p className="text-sm uppercase tracking-wide text-[#059669] font-semibold mb-3">{s.tagline}</p>
            <p className="text-[#475569] text-sm leading-relaxed mb-5 flex-1">{s.short}</p>
            <span className="inline-flex items-center gap-2 font-bold uppercase text-sm tracking-wide text-[#0F2E4C] group-hover:text-[#059669] transition-colors">
              Explore {s.name} <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
            </span>
          </div>
        </Link>
      );
    })}
  </div>
);

// ---------- Testimonials ----------
export const TestimonialGrid = ({ limit }) => {
  const list = limit ? testimonials.slice(0, limit) : testimonials;
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="testimonial-grid">
      {list.map((t, i) => (
        <div key={i} className="bg-white border border-[#E2E8F0] p-6 hover:shadow-[4px_4px_0px_#0F2E4C] transition-shadow" data-testid={`testimonial-${i}`}>
          <div className="flex gap-1 mb-3">
            {Array.from({ length: t.rating }).map((_, j) => <Star key={j} className="w-5 h-5 fill-[#EAB308] text-[#EAB308]" />)}
          </div>
          <p className="text-[#0F2E4C] leading-relaxed mb-5">"{t.text}"</p>
          <div className="border-t border-[#E2E8F0] pt-4">
            <p className="font-bold">{t.name}</p>
            <p className="text-sm text-[#475569]">{t.location} · {t.service}</p>
          </div>
        </div>
      ))}
    </div>
  );
};

// ---------- Project gallery (filterable before/after) ----------
export const ProjectGallery = ({ showFilter = true, filterCategory = null }) => {
  const [active, setActive] = useState("all");
  const cat = filterCategory || active;
  const filtered = cat === "all" ? projects : projects.filter((p) => p.category === cat);
  const filters = [{ key: "all", label: "All Work" }, ...services.map((s) => ({ key: s.slug, label: s.name }))];

  return (
    <div data-testid="project-gallery">
      {showFilter && (
        <div className="flex flex-wrap gap-2 mb-8 justify-center">
          {filters.map((f) => (
            <button
              key={f.key}
              onClick={() => setActive(f.key)}
              className={`px-5 py-2 text-sm font-bold uppercase tracking-wide border-2 transition-colors ${active === f.key ? "bg-[#0F2E4C] text-white border-[#0F2E4C]" : "bg-white text-[#0F2E4C] border-[#E2E8F0] hover:border-[#0F2E4C]"}`}
              data-testid={`gallery-filter-${f.key}`}
            >
              {f.label}
            </button>
          ))}
        </div>
      )}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        {filtered.map((p) => (
          <div key={p.id} className="bg-white border border-[#E2E8F0] p-4" data-testid={`project-${p.id}`}>
            <BeforeAfterSlider before={p.before} after={p.after} beforeAlt={`Before: ${p.caption}`} afterAlt={`After: ${p.caption}`} />
            <div className="pt-4">
              <span className="inline-block bg-[#059669] text-white text-xs font-bold uppercase tracking-wide px-2 py-1 mb-2 capitalize">{p.category}</span>
              <p className="text-[#0F2E4C] font-medium leading-relaxed mb-3">{p.caption}</p>
              <EstimateButton size="sm" label="Request a Free Estimate" testid={`project-cta-${p.id}`} />
            </div>
          </div>
        ))}
      </div>
      <p className="text-xs text-[#475569] text-center mt-6 italic">Sample project images are AI-generated for demonstration purposes.</p>
    </div>
  );
};
